/**
 * The store renaming a document it did NOT switch.
 *
 * There is exactly one of these in the app. A paid pass on the worked example
 * remints `session.id` from `s_demo` to a fresh one — see `beginOp` — because a
 * demo somebody has spent money on is theirs and stops being the demo. The
 * document on screen does not change: same seed, same nodes, same graph, new
 * name.
 *
 * From outside the store that rename is indistinguishable from every other way
 * an exploration's id changes — `restoreSession`, the replay path, forking —
 * and a layer that tried to tell them apart by watching the id got it wrong.
 * That was a real bug and it was reverted, not narrowed: a conversation was
 * moved into whatever exploration was being opened, attaching it to a graph it
 * was not about. The lesson was not "guess more carefully". It was that the only
 * component that KNOWS a rename happened is the one performing it, so it has to
 * say so.
 *
 * So this is the saying. `beginOp` announces; anything that keys state by
 * exploration id subscribes and follows. Before it existed, the one follower we
 * had — the coach — was a special case written inline in `beginOp`, and every
 * new follower meant another line there and another chance to get the condition
 * wrong.
 *
 * **Synchronous, deliberately.** A subscriber may be holding an in-flight
 * request whose key must be rewritten before the next delta lands, so this
 * cannot wait for a render or a microtask. Subscribers are called in
 * registration order over a copy of the set, so one that unsubscribes itself
 * mid-announcement does not disturb the rest.
 *
 * This module imports nothing. It sits under `store.ts` and under `core`'s
 * storage leaves alike, so either may depend on it without a cycle.
 */

/** One document, renamed in place. Never two different documents. */
export interface Rename {
  from: string
  to: string
}

const watchers = new Set<(r: Rename) => void>()

/**
 * Say that a document has been renamed.
 *
 * Called by the store, and by nothing else — a rename announced by a layer that
 * only observed one would be the guess this module exists to replace.
 *
 * A no-op when the name has not actually changed, so callers may announce
 * unconditionally rather than each repeating the comparison.
 */
export function announceRename(from: string, to: string): void {
  if (from === to) return
  const r: Rename = { from, to }
  for (const fn of [...watchers]) fn(r)
}

/** Follow renames. Returns the unsubscribe. */
export function onRenamed(fn: (r: Rename) => void): () => void {
  watchers.add(fn)
  return () => {
    watchers.delete(fn)
  }
}
